/**
 * Generic type-safe storage service for localStorage/sessionStorage with fallback shim.
 */

export interface IStorageService<T> {
  get<K extends keyof T>(key: K): T[K] | null;
  set<K extends keyof T>(key: K, value: T[K]): void;
  remove<K extends keyof T>(key: K): void;
  clear(): void;
}

class StorageShim {
  private hash: Record<string, any> = {};
  getItem(key: string) { return this.hash[key]; }
  setItem(key: string, value: any) { this.hash[key] = value; }
  removeItem(key: string) { delete this.hash[key]; }
  clear() { this.hash = {}; }
}

export class StorageService<T> implements IStorageService<T> {
  private storage: Storage | StorageShim;

  constructor(storage?: Storage) {
    this.storage = storage || new StorageShim();
  }

  set<K extends keyof T>(key: K, value: T[K]): void {
    try {
      this.storage.setItem(key.toString(), JSON.stringify(value));
    } catch {}
  }

  get<K extends keyof T>(key: K): T[K] | null {
    try {
      const value = this.storage.getItem(key.toString());
      if (
        value === null || value === 'null' ||
        value === undefined || value === 'undefined'
      ) {
        return null;
      }
      // Always parse if value is a string
      if (typeof value === 'string') {
        return JSON.parse(value) as T[K];
      }
      return value as T[K];
    } catch {
      return null;
    }
  }

  remove<K extends keyof T>(key: K): void {
    try {
      this.storage.removeItem(key.toString());
    } catch {}
  }

  clear(): void {
    try {
      this.storage.clear();
    } catch {}
  }
}

// Factory functions for safe instantiation
export function createStorageService<T>(storage: Storage | undefined): StorageService<T> {
  return new StorageService<T>(storage);
}

export function createSafeStorage<T>(getStorage: () => Storage): StorageService<T> {
  let storage: Storage | undefined;
  try {
    storage = getStorage();
  } catch {
    storage = undefined;
  }
  return new StorageService<T>(storage);
}